import React, { useState } from 'react'
import { Modal, Pressable, StyleSheet, View } from 'react-native'
import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { DS } from '@/design-system'
import { useAsyncMutation } from './useAsyncMutation'

export type CohostTarget = {
  userId: string
  userName: string
  isCohost: boolean
}

interface Props {
  visible: boolean
  onClose: () => void
  target: CohostTarget | null
  tournamentId: Id<'tournaments'>
  /** Auth0 subject of the host taking the action. */
  hostUserId: string
}

/**
 * Host-only modal for promoting/demoting a co-host.
 *
 * Three actions:
 *  - Make Co-host (when target is a registered, non-cohost player)
 *  - Make Host (transferHost — destructive; current host becomes co-host)
 *  - Remove Co-host
 *
 * TODO(ux): the "Make Host" action is destructive. Per code review, we'll
 * ship the current copy and revisit with updated UX (e.g. typed-confirmation
 * step) in a follow-up PR. Today the only mitigation is that the action is
 * gated to `isCohost` targets and behind two taps.
 */
export function CohostManagementModal({ visible, onClose, target, tournamentId, hostUserId }: Props) {
  const addCohostMutation = useMutation(api.tournaments.hostManagement.addCohost)
  const removeCohostMutation = useMutation(api.tournaments.hostManagement.removeCohost)
  const transferHostMutation = useMutation(api.tournaments.hostManagement.transferHost)

  const addCohost = useAsyncMutation(addCohostMutation, { onSuccess: onClose })
  const removeCohost = useAsyncMutation(removeCohostMutation, { onSuccess: onClose })
  const transferHost = useAsyncMutation(transferHostMutation, { onSuccess: onClose })

  const anyPending = addCohost.pending || removeCohost.pending || transferHost.pending

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={() => {
        if (!anyPending) onClose()
      }}
    >
      <Pressable
        style={styles.modalBackdrop}
        onPress={() => {
          if (!anyPending) onClose()
        }}
      >
        <Pressable style={styles.cohostModal} onPress={(e) => e.stopPropagation()}>
          <DS.Text variant="Heading 3/Medium" style={{ textAlign: 'center', marginBottom: 4 }}>
            {target?.userName}
          </DS.Text>
          <DS.Text
            variant="Body Large/Regular"
            style={{ textAlign: 'center', marginBottom: 24, color: DS.palette['Brand/Dark Grey'] }}
          >
            {target?.isCohost ? 'This player is currently a co-host.' : 'Make this player a co-host?'}
          </DS.Text>
          <DS.Frame gap="8">
            {target?.isCohost ? (
              <>
                <DS.Button
                  type="On Light"
                  variant="Large/Primary"
                  backgroundColor="Brand/Maroon"
                  disabled={anyPending}
                  onPress={() => {
                    if (!target) return
                    transferHost.run({ tournamentId, userId: hostUserId, targetUserId: target.userId })
                  }}
                >
                  Make Host
                </DS.Button>
                <DS.Button
                  type="On Light"
                  variant="Large/Primary"
                  backgroundColor="Brand/Maroon"
                  disabled={anyPending}
                  onPress={() => {
                    if (!target) return
                    removeCohost.run({ tournamentId, userId: hostUserId, targetUserId: target.userId })
                  }}
                >
                  Remove Co-host
                </DS.Button>
              </>
            ) : (
              <DS.Button
                type="On Light"
                variant="Large/Primary"
                backgroundColor="Brand/Maroon"
                disabled={anyPending}
                onPress={() => {
                  if (!target) return
                  addCohost.run({ tournamentId, userId: hostUserId, targetUserId: target.userId })
                }}
              >
                Make Co-host
              </DS.Button>
            )}
            <DS.Button type="On Dark" variant="Large/Secondary" onPress={onClose} disabled={anyPending}>
              Cancel
            </DS.Button>
          </DS.Frame>
        </Pressable>
      </Pressable>
    </Modal>
  )
}

const styles = StyleSheet.create({
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center'
  },
  cohostModal: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 16,
    padding: 24,
    width: '90%',
    maxWidth: 400
  }
})
